import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Sparkle, ImageSquare, MicrophoneStage, MagicWand, UploadSimple, Check, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (v) => (!v ? '' : (/^https?:\/\//i.test(v) ? v : `${API_URL}${v}`));

const BG_OPTIONS = [
  { id: 'none',  label: 'None',           hint: 'Show your real background' },
  { id: 'blur',  label: 'Blur',           hint: 'Privacy-friendly blur' },
  { id: 'image', label: 'Custom Image',   hint: 'Upload your own image' },
  { id: 'ai',    label: 'AI Generated',   hint: 'Generate from a prompt ✨' },
];
const FILTER_OPTIONS = [
  { id: 'off',    label: 'Off',     hint: 'No filter — natural look' },
  { id: 'soft',   label: 'Soft',    hint: 'Light skin smoothing + brightness' },
  { id: 'studio', label: 'Studio',  hint: 'Stronger smoothing + colour boost' },
];

const StudioSettingsCard = () => {
  const [settings, setSettings] = useState({ background_type: 'none', background_url: '', noise_suppression: true, camera_filter: 'off' });
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/studio/settings`);
      setSettings(res.data || settings);
    } catch (e) { /* not a seller; component will return null */ }
    finally { setLoaded(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const save = async (next) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/studio/settings`, next);
      setSettings(res.data);
      toast.success('Studio updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const generateAi = async () => {
    if (!aiPrompt.trim()) return toast.error('Describe the background you want');
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/studio/generate-background`, { prompt: aiPrompt.trim() });
      const url = res.data?.background_url;
      if (url) {
        await save({ ...settings, background_type: 'ai', background_url: url });
        toast.success('AI background ready');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Max 5MB');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/studio/upload-background`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data?.background_url;
      if (url) await save({ ...settings, background_type: 'image', background_url: url });
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  if (!loaded) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="studio-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkle size={18} weight="fill" className="text-pink-500" />
        <h3 className="text-base font-bold text-slate-900">Live Session Studio</h3>
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-pink-50 text-pink-600 font-bold">AI</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Configure your camera + audio once — applied automatically every time you start a class.</p>

      {/* Background picker */}
      <div className="mb-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2"><ImageSquare size={16} /> Virtual Background</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {BG_OPTIONS.map(opt => {
            const active = settings.background_type === opt.id;
            return (
              <button key={opt.id} onClick={() => save({ ...settings, background_type: opt.id })}
                data-testid={`bg-${opt.id}`}
                className={`px-3 py-2.5 rounded-xl text-xs text-left transition-colors border ${active ? 'border-pink-400 bg-pink-50 text-pink-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                <div className="font-semibold flex items-center gap-1">{opt.label} {active && <Check size={12} weight="bold" />}</div>
                <div className="text-[10px] opacity-70 mt-0.5">{opt.hint}</div>
              </button>
            );
          })}
        </div>

        {/* AI prompt */}
        {settings.background_type === 'ai' && (
          <div className="mt-3 p-3 bg-pink-50/40 rounded-xl border border-pink-100" data-testid="ai-bg-panel">
            <div className="flex gap-2 flex-wrap">
              <Input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g. minimalist yoga studio with soft morning light"
                className="flex-1 min-w-[200px] h-9 text-sm" data-testid="ai-prompt-input" />
              <Button size="sm" onClick={generateAi} disabled={generating || !aiPrompt.trim()}
                className="bg-pink-500 hover:bg-pink-600 h-9 rounded-lg text-xs" data-testid="generate-ai-bg-btn">
                <MagicWand size={14} className="mr-1" /> {generating ? 'Generating…' : 'Generate'}
              </Button>
              {settings.background_url && (
                <Button size="sm" variant="outline" onClick={generateAi} disabled={generating || !aiPrompt.trim()}
                  className="h-9 rounded-lg text-xs" data-testid="regen-ai-bg-btn" title="Regenerate">
                  <ArrowsClockwise size={14} />
                </Button>
              )}
            </div>
            <p className="text-[10px] text-pink-700/70 mt-1.5">Uses Gemini Nano Banana — takes 5–15 seconds. Generated images are 16:9.</p>
          </div>
        )}

        {/* Upload */}
        {settings.background_type === 'image' && (
          <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200" data-testid="upload-bg-panel">
            <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-300 text-slate-700 text-xs font-medium cursor-pointer bg-white hover:bg-slate-100" data-testid="upload-bg-label">
              <UploadSimple size={14} />
              {uploading ? 'Uploading…' : 'Choose Image'}
              <input type="file" accept="image/png,image/jpeg,image/webp"
                onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} className="hidden" data-testid="upload-bg-input" />
            </label>
          </div>
        )}

        {/* Preview */}
        {settings.background_url && ['image', 'ai'].includes(settings.background_type) && (
          <div className="mt-3" data-testid="bg-preview-wrapper">
            <img src={resolveUrl(settings.background_url)} alt="Background preview"
              className="w-full max-w-md aspect-video rounded-xl object-cover border border-slate-200" data-testid="bg-preview-img" />
          </div>
        )}
      </div>

      {/* Voice */}
      <div className="mb-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2"><MicrophoneStage size={16} /> Voice Quality</div>
        <label className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid="noise-suppression-label">
          <input type="checkbox" checked={settings.noise_suppression}
            onChange={(e) => save({ ...settings, noise_suppression: e.target.checked })}
            className="h-4 w-4 accent-pink-500" data-testid="noise-suppression-toggle" />
          <span className="text-sm text-slate-800 font-medium">Noise suppression + echo cancellation</span>
          <span className="text-[10px] text-slate-500 ml-auto">Recommended</span>
        </label>
      </div>

      {/* Filter */}
      <div className="mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2"><Sparkle size={16} /> Camera Filter</div>
        <div className="grid grid-cols-3 gap-2">
          {FILTER_OPTIONS.map(opt => {
            const active = settings.camera_filter === opt.id;
            return (
              <button key={opt.id} onClick={() => save({ ...settings, camera_filter: opt.id })}
                data-testid={`filter-${opt.id}`}
                className={`px-3 py-2.5 rounded-xl text-xs text-left transition-colors border ${active ? 'border-pink-400 bg-pink-50 text-pink-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                <div className="font-semibold flex items-center gap-1">{opt.label} {active && <Check size={12} weight="bold" />}</div>
                <div className="text-[10px] opacity-70 mt-0.5">{opt.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {saving && <p className="text-[11px] text-slate-400 mt-2">Saving…</p>}
    </div>
  );
};

export default StudioSettingsCard;
